/**
 * Global keyboard handler manager to prevent duplicate event listeners
 * and provide debouncing for keyboard shortcuts.
 * 
 * This singleton ensures only one handler per key combination is registered,
 * preventing the double-execution issue in React StrictMode.
 */

interface KeyboardHandler {
  id: string;
  keys: string[];
  handler: (event: KeyboardEvent) => void;
  options?: {
    preventDefault?: boolean;
    stopPropagation?: boolean;
    capture?: boolean;
    debounceMs?: number;
  };
}

interface DebounceState {
  lastExecution: number;
  timeoutId?: number;
}

class KeyboardHandlerManager {
  private static instance: KeyboardHandlerManager;
  private handlers: Map<string, KeyboardHandler> = new Map();
  private debounceStates: Map<string, DebounceState> = new Map();
  private globalListener: ((event: KeyboardEvent) => void) | null = null;
  private isListenerRegistered = false;

  private constructor() {}

  public static getInstance(): KeyboardHandlerManager {
    if (!KeyboardHandlerManager.instance) {
      KeyboardHandlerManager.instance = new KeyboardHandlerManager();
    }
    return KeyboardHandlerManager.instance;
  }

  /**
   * Register a keyboard shortcut handler
   */
  public register(handler: KeyboardHandler): () => void {
    const keySignature = this.createKeySignature(handler.keys);
    
    // If handler already exists for this key combination, remove it first
    if (this.handlers.has(keySignature)) {
      console.warn(`[KeyboardHandlerManager] Replacing existing handler for ${keySignature}`);
      this.unregister(keySignature);
    }

    this.handlers.set(keySignature, handler);
    
    // Ensure global listener is registered
    this.ensureGlobalListener();

    // Return unregister function
    return () => this.unregister(keySignature);
  }

  /**
   * Unregister a keyboard shortcut handler
   */
  public unregister(keySignatureOrId: string): void {
    // Try to find by key signature first, then by handler ID
    let keyToRemove: string | undefined;
    
    if (this.handlers.has(keySignatureOrId)) {
      keyToRemove = keySignatureOrId;
    } else {
      // Search by handler ID
      for (const [key, handler] of this.handlers.entries()) {
        if (handler.id === keySignatureOrId) {
          keyToRemove = key;
          break;
        }
      }
    }

    if (keyToRemove) {
      this.handlers.delete(keyToRemove);
      
      // Clear any pending debounce for this handler
      const debounceState = this.debounceStates.get(keyToRemove);
      if (debounceState?.timeoutId) {
        clearTimeout(debounceState.timeoutId);
      }
      this.debounceStates.delete(keyToRemove);
    }

    // If no handlers remain, remove global listener
    if (this.handlers.size === 0) {
      this.removeGlobalListener();
    }
  }

  /**
   * Clear all handlers and remove global listener
   */
  public clear(): void {
    // Clear all debounce timeouts
    for (const debounceState of this.debounceStates.values()) {
      if (debounceState.timeoutId) {
        clearTimeout(debounceState.timeoutId);
      }
    }
    
    this.handlers.clear();
    this.debounceStates.clear();
    this.removeGlobalListener();
  }

  /**
   * Get info about registered handlers (for debugging)
   */
  public getHandlerInfo(): Array<{ keySignature: string; handlerId: string }> {
    return Array.from(this.handlers.entries()).map(([key, handler]) => ({
      keySignature: key,
      handlerId: handler.id
    }));
  }

  private createKeySignature(keys: string[]): string {
    return keys.sort().join('+').toLowerCase();
  }

  private ensureGlobalListener(): void {
    if (!this.isListenerRegistered) {
      this.globalListener = this.handleGlobalKeydown.bind(this);
      window.addEventListener('keydown', this.globalListener, true);
      this.isListenerRegistered = true;
    }
  }

  private removeGlobalListener(): void {
    if (this.globalListener && this.isListenerRegistered) {
      window.removeEventListener('keydown', this.globalListener, true);
      this.globalListener = null;
      this.isListenerRegistered = false;
    }
  }

  private handleGlobalKeydown(event: KeyboardEvent): void {
    const pressedKeys: string[] = [];
    
    // Add modifier keys
    if (event.metaKey || event.ctrlKey) pressedKeys.push('cmd');
    if (event.altKey) pressedKeys.push('alt');
    if (event.shiftKey) pressedKeys.push('shift');
    
    // Add main key (normalize common variations)
    if (event.key && !['Control', 'Alt', 'Shift', 'Meta', 'Command'].includes(event.key)) {
      let key = event.key.toLowerCase();
      
      // Normalize key names
      if (key === ' ') key = 'space';
      if (key.startsWith('arrow')) key = key.replace('arrow', '');
      
      pressedKeys.push(key);
    }

    if (pressedKeys.length === 0) return;

    const keySignature = this.createKeySignature(pressedKeys);
    const handler = this.handlers.get(keySignature);

    if (handler) {
      // Handle options
      const options = handler.options || {};
      
      if (options.preventDefault !== false) {
        event.preventDefault();
      }
      
      if (options.stopPropagation !== false) {
        event.stopPropagation();
      }

      // Handle debouncing
      if (options.debounceMs && options.debounceMs > 0) {
        this.executeWithDebounce(keySignature, handler, event, options.debounceMs);
      } else {
        handler.handler(event);
      }
    }
  }

  private executeWithDebounce(
    keySignature: string, 
    handler: KeyboardHandler, 
    event: KeyboardEvent, 
    debounceMs: number
  ): void {
    const now = Date.now();
    let debounceState = this.debounceStates.get(keySignature);

    if (!debounceState) {
      debounceState = { lastExecution: 0 };
      this.debounceStates.set(keySignature, debounceState);
    }

    // If we're within the debounce window, clear the previous timeout and set a new one
    if (debounceState.timeoutId) {
      clearTimeout(debounceState.timeoutId);
    }

    // If enough time has passed since last execution, execute immediately
    if (now - debounceState.lastExecution > debounceMs) {
      debounceState.lastExecution = now;
      handler.handler(event);
    } else {
      // Otherwise, debounce the execution
      debounceState.timeoutId = window.setTimeout(() => {
        debounceState!.lastExecution = Date.now();
        handler.handler(event);
        debounceState!.timeoutId = undefined;
      }, debounceMs);
    }
  }
}

// Export singleton instance
export const keyboardHandlerManager = KeyboardHandlerManager.getInstance();